import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    getStoredTokens,
    getStoredAccountInfo,
    isServiceAuthorized,
    revokeGoogleAccess,
    loadTokensFromCloud,
    GoogleAccountInfo,
    GoogleService,
} from '../services/googleAuth';

// Integration status
export interface IntegrationStatus {
    id: string;
    name: string;
    service: GoogleService | 'slack' | 'notion' | 'linear' | 'todoist' | 'asana' | 'trello' | 'dropbox' | 'drive';
    connected: boolean;
    accountInfo?: GoogleAccountInfo;
    lastSync?: string;
}

// Context type
interface IntegrationsContextType {
    integrations: IntegrationStatus[];
    isGoogleConnected: boolean;
    googleAccount: GoogleAccountInfo | null;
    isServiceConnected: (service: GoogleService) => boolean;
    refreshIntegrationStatus: () => void;
    disconnectGoogle: () => Promise<void>;
    updateLastSync: (integrationId: string) => void;
}

const IntegrationsContext = createContext<IntegrationsContextType | null>(null);

// Default integrations list
const DEFAULT_INTEGRATIONS: IntegrationStatus[] = [
    { id: 'google-calendar', name: 'Google Calendar', service: 'calendar', connected: false },
    { id: 'gmail', name: 'Gmail', service: 'gmail', connected: false },
    { id: 'slack', name: 'Slack', service: 'slack', connected: false },
    { id: 'notion', name: 'Notion', service: 'notion', connected: false },
    { id: 'linear', name: 'Linear', service: 'linear', connected: false },
    { id: 'todoist', name: 'Todoist', service: 'todoist', connected: false },
    { id: 'asana', name: 'Asana', service: 'asana', connected: false },
    { id: 'trello', name: 'Trello', service: 'trello', connected: false },
    { id: 'dropbox', name: 'Dropbox', service: 'dropbox', connected: false },
    { id: 'drive', name: 'Google Drive', service: 'drive', connected: false },
];

const STORAGE_KEY = 'way2will_integrations';

export function IntegrationsProvider({ children }: { children: ReactNode }) {
    const [integrations, setIntegrations] = useState<IntegrationStatus[]>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : DEFAULT_INTEGRATIONS;
    });
    const [googleAccount, setGoogleAccount] = useState<GoogleAccountInfo | null>(null);

    // Load tokens from cloud and check Google connection status on mount
    useEffect(() => {
        const init = async () => {
            // Try to load tokens from Firestore (for cross-device persistence)
            await loadTokensFromCloud();
            // Then refresh integration status with whatever we have
            refreshIntegrationStatus();
        };
        init();
    }, []);

    // Persist integrations to localStorage
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(integrations));
    }, [integrations]);

    const refreshIntegrationStatus = () => {
        const tokens = getStoredTokens();
        const account = getStoredAccountInfo();

        if (account) {
            setGoogleAccount(account);
        }

        setIntegrations(prev => prev.map(integration => {
            // Check Google services
            if (integration.service === 'calendar' || integration.service === 'gmail') {
                const isConnected = tokens ? isServiceAuthorized(integration.service) : false;
                return {
                    ...integration,
                    connected: isConnected,
                    accountInfo: isConnected ? account || undefined : undefined,
                    lastSync: isConnected ? integration.lastSync || 'Just now' : undefined,
                };
            }
            return integration;
        }));
    };

    const isGoogleConnected = !!getStoredTokens();

    const isServiceConnected = (service: GoogleService): boolean => {
        return isServiceAuthorized(service);
    };

    const disconnectGoogle = async (): Promise<void> => {
        await revokeGoogleAccess();
        setGoogleAccount(null);

        // Update integrations to disconnected
        setIntegrations(prev => prev.map(integration => {
            if (integration.service === 'calendar' || integration.service === 'gmail') {
                return {
                    ...integration,
                    connected: false,
                    accountInfo: undefined,
                    lastSync: undefined,
                };
            }
            return integration;
        }));
    };

    const updateLastSync = (integrationId: string) => {
        setIntegrations(prev => prev.map(integration => {
            if (integration.id === integrationId) {
                return {
                    ...integration,
                    lastSync: 'Just now',
                };
            }
            return integration;
        }));
    };

    return (
        <IntegrationsContext.Provider
            value={{
                integrations,
                isGoogleConnected,
                googleAccount,
                isServiceConnected,
                refreshIntegrationStatus,
                disconnectGoogle,
                updateLastSync,
            }}
        >
            {children}
        </IntegrationsContext.Provider>
    );
}

export function useIntegrations() {
    const context = useContext(IntegrationsContext);
    if (!context) {
        throw new Error('useIntegrations must be used within an IntegrationsProvider');
    }
    return context;
}
